import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { prompts } from "./TaxHelpers";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { History, Plus, ShieldCheck, Trash2 } from "lucide-react";

import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { disableTdsConfig, getTdsConfig, saveTdsConfig } from "@/services/adminService";
import { toast } from "sonner";

const TdsConfiguration = ({
  handleCancel,
  savingSection,
  canEditTaxConfig,
}) => {
  const tabId = "tds";
  const prompt = prompts[tabId];

  const [activeTab, setActiveTab] = useState("slabs");
  const [loadingConfig, setLoadingConfig] = useState(true);

  useEffect(() => {
    async function loadConfig() {
      try {
        const res = await getTdsConfig();
        if (res?.data) {
          // API returns: { fy, generalRules, newRegimeSlabs, oldRegimeSlabs, exemptions }
          reset({
            fy: res.data.fy || getCurrentFinancialYear(),

            generalRules: {
              cess: res.data.generalRules?.cess ?? 4,
              penalty: res.data.generalRules?.penalty ?? 20,
              newRegimeStd: res.data.generalRules?.newRegimeStd ?? 75000,
              oldRegimeStd: res.data.generalRules?.oldRegimeStd ?? 50000,
              newRegimeRebateLimit:
                res.data.generalRules?.newRegimeRebateLimit ?? 1200000,
              oldRegimeRebateLimit:
                res.data.generalRules?.oldRegimeRebateLimit ?? 500000,
            },

            newRegimeSlabs: res.data.newRegimeSlabs?.length
              ? res.data.newRegimeSlabs
              : getValues("newRegimeSlabs"),

            oldRegimeSlabs: res.data.oldRegimeSlabs?.length
              ? res.data.oldRegimeSlabs
              : getValues("oldRegimeSlabs"),

            exemptions: res.data.exemptions?.length
              ? res.data.exemptions
              : getValues("exemptions"),
          });
        }
      } catch (e) {
        console.warn("No existing TDS config — Using defaults");
      } finally {
        setLoadingConfig(false);
      }
    }

    loadConfig();
  }, []);

  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    reset,
    formState: { isSubmitting },
  } = useForm({
    defaultValues: {
      fy: "2025-2026",
      generalRules: {
        cess: 4,
        penalty: 20,
        newRegimeStd: 75000,
        oldRegimeStd: 50000,
        newRegimeRebateLimit: 1200000,
        oldRegimeRebateLimit: 500000,
      },
      newRegimeSlabs: [
        { min: 0, max: 400000, rate: 0 },
        { min: 400001, max: 800000, rate: 5 },
        { min: 800001, max: 1200000, rate: 10 },
        { min: 1200001, max: 1600000, rate: 15 },
        { min: 1600001, max: 2000000, rate: 20 },
        { min: 2000001, max: 2400000, rate: 25 },
        { min: 2400001, max: null, rate: 30 },
      ],
      oldRegimeSlabs: [
        { min: 0, max: 250000, rate: 0 },
        { min: 250001, max: 500000, rate: 5 },
        { min: 500001, max: 1000000, rate: 20 },
        { min: 1000001, max: null, rate: 30 },
      ],
      exemptions: [
        {
          id: 1,
          name: "House Rent Allowance (HRA)",
          section: "Sec 10(13A)",
          newRegime: false,
          oldRegime: true,
        },
        {
          id: 2,
          name: "Standard Deduction",
          section: "Sec 16(ia)",
          newRegime: true,
          oldRegime: true,
        },
        {
          id: 3,
          name: "PF / LIC / PPF Investments",
          section: "Sec 80C",
          newRegime: false,
          oldRegime: true,
        },
      ],
    },
  });

  const newRegimeSlabs = watch("newRegimeSlabs");
  const oldRegimeSlabs = watch("oldRegimeSlabs");
  const exemptions = watch("exemptions");

  const updateSlab = (index, field, value, isNewRegime) => {
    const key = isNewRegime ? "newRegimeSlabs" : "oldRegimeSlabs";
    const slabs = [...getValues(key)];
    const numericValue = value === "" ? "" : Number(value);

    if (field === "max" && (value === "" || numericValue === 0)) {
      slabs[index].max = null;
    } else {
      slabs[index][field] = numericValue;
    }
    setValue(key, slabs, { shouldDirty: true });
  };

  const addSlab = (isNewRegime) => {
    const key = isNewRegime ? "newRegimeSlabs" : "oldRegimeSlabs";
    const slabs = [...getValues(key)];
    let lastSlab = slabs[slabs.length - 1];

    if (lastSlab.max === null) {
      lastSlab = { ...lastSlab, max: lastSlab.min + 300000 };
      slabs[slabs.length - 1] = lastSlab;
    }

    const newMin = (lastSlab.max || 0) + 1;
    const newRate = lastSlab.rate === 30 ? 30 : lastSlab.rate + 5;

    slabs.push({ min: newMin, max: null, rate: newRate });
    setValue(key, slabs, { shouldDirty: true });
  };

  const deleteSlab = (index, isNewRegime) => {
    const key = isNewRegime ? "newRegimeSlabs" : "oldRegimeSlabs";
    const slabs = [...getValues(key)];
    if (slabs.length <= 1) return;

    const wasLast = index === slabs.length - 1;
    slabs.splice(index, 1);

    if (wasLast && slabs.length > 0) {
      slabs[slabs.length - 1].max = null;
    }

    setValue(key, slabs, { shouldDirty: true });
  };

  const toggleExemption = (index, field) => {
    const list = [...getValues("exemptions")];
    const ex = list[index];
    list[index] = { ...ex, [field]: !ex[field] };
    setValue("exemptions", list, { shouldDirty: true });
  };

  const onSubmit = async (data) => {
    if (!canEditTaxConfig) {
      toast.error("You do not have permission to modify TDS configuration.");
      return;
    }
    const invalidRows = [];

    // ✅ Force last slab max = null (mutates a cloned structure, not RHF internal ref)
    const normalizeLastSlabMaxNull = (slabs) => {
      if (!Array.isArray(slabs) || slabs.length === 0) return slabs;

      const copy = slabs.map((s) => ({ ...s }));
      const lastIndex = copy.length - 1;

      copy[lastIndex] = {
        ...copy[lastIndex],
        max: null,
      };

      return copy;
    };

    // ✅ Create a sanitized payload to validate + save
    const payload = {
      ...data,
      newRegimeSlabs: normalizeLastSlabMaxNull(data?.newRegimeSlabs),
      oldRegimeSlabs: normalizeLastSlabMaxNull(data?.oldRegimeSlabs),
    };

    const checkSlabs = (slabs, label) => {
      slabs?.forEach((slab, i) => {
        const min = Number(slab?.min);
        const rawMax = slab?.max;

        const isLastRow = i === (slabs?.length || 0) - 1;

        const hasMin = !Number.isNaN(min);

        // Max is allowed to be null ONLY for last row
        const maxIsNull =
          rawMax === null || rawMax === undefined || rawMax === "";
        const maxNum = Number(rawMax);
        const hasMaxNumber = !Number.isNaN(maxNum);

        // 1) Max must be >= Min (ONLY if max is a valid number)
        //    - last row may have max = null, so skip this check
        if (hasMin && !maxIsNull && hasMaxNumber && maxNum < min) {
          invalidRows.push({
            type: "max_lt_min",
            label,
            row: i + 1,
            min,
            max: maxNum,
          });
        }

        // 2) Min must be > previous slab's Max
        //    - if previous max is null, we cannot validate continuity, so skip
        if (i > 0) {
          const prevRawMax = slabs[i - 1]?.max;
          const prevMaxIsNull =
            prevRawMax === null ||
            prevRawMax === undefined ||
            prevRawMax === "";
          const prevMaxNum = Number(prevRawMax);
          const hasPrevMaxNumber = !Number.isNaN(prevMaxNum);

          if (
            hasMin &&
            !prevMaxIsNull &&
            hasPrevMaxNumber &&
            min <= prevMaxNum
          ) {
            invalidRows.push({
              type: "min_not_greater_than_prev_max",
              label,
              row: i + 1,
              min,
              prevMax: prevMaxNum,
            });
          }
        }

        // 3) Enforce that ONLY last row can have max = null
        if (!isLastRow && maxIsNull) {
          invalidRows.push({
            type: "max_null_not_last",
            label,
            row: i + 1,
          });
        }
      });
    };

    checkSlabs(payload?.newRegimeSlabs, "New Regime");
    checkSlabs(payload?.oldRegimeSlabs, "Old Regime");

    if (invalidRows.length > 0) {
      const first = invalidRows[0];

      if (first.type === "max_lt_min") {
        toast.error(
          `${first.label}: Row ${first.row} is invalid (Max ₹${first.max} < Min ₹${first.min}). Please correct it.`,
        );
        return;
      }

      if (first.type === "min_not_greater_than_prev_max") {
        toast.error(
          `${first.label}: Row ${first.row} is invalid (Min ₹${first.min} must be greater than previous Max ₹${first.prevMax}). Please correct it.`,
        );
        return;
      }

      if (first.type === "max_null_not_last") {
        toast.error(
          `${first.label}: Row ${first.row} is invalid (Only the last slab can have Max as empty). Please correct it.`,
        );
        return;
      }

      toast.error("Invalid slab configuration. Please correct and try again.");
      return;
    }

    try {
      await saveTdsConfig(payload); // ✅ save sanitized payload
      toast.success("TDS configuration saved successfully!");
    } catch (err) {
      toast.error("Failed to save TDS configuration");
    }
  };

  const getCurrentFinancialYear = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1; // 1 = Jan, 12 = Dec

    // FY starts on April 1
    if (month >= 4) {
      return `${year}-${year + 1}`;
    } else {
      return `${year - 1}-${year}`;
    }
  };

  const handleDisable = async () => {
    if (!canEditTaxConfig) {
      toast.error("You do not have permission to disable TDS.");
      return;
    }
    try {
      const response = await disableTdsConfig({
        enabled: false,
        fy: getCurrentFinancialYear(),
      });

      toast.success("TDS disabled successfully!");
      handleCancel();
    } catch (error) {
      toast.error("Failed to disable TDS");
    }
  };

  if (loadingConfig) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-28 w-full" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </div>
    );
  }
  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center bg-secondary dark:bg-slate-800 p-4 rounded-xl border dark:border-slate-700">
          <div>
            <h3 className="text-lg font-bold text-gray-700 dark:text-gray-200">
              TDS Policy Rules
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Global tax rules applied to all payroll calculations
            </p>
          </div>

          <div className="flex gap-3 text-gray-600 dark:text-gray-50 font-bold">
            FY {getCurrentFinancialYear()}
          </div>
        </div>

        {/* Overview Box */}
        <div className="p-6 border-l-4 border-l-blue-500 rounded-lg bg-blue-50 dark:bg-blue-900/20 dark:border-blue-400">
          <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-200 mb-2">
            TDS Configuration Overview
          </h3>
          <p className="text-sm text-blue-700 dark:text-blue-300">
            {prompt.description} Configure tax rules, slabs & exemptions here.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-slate-200 dark:border-slate-700 pb-1 overflow-x-auto">
          {["general", "slabs", "exemptions"].map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={
                () => setActiveTab(tab) // tab is "general" | "slabs" | "exemptions"
              }
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
                activeTab === tab
                  ? "bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 border-b-white dark:border-b-slate-900 text-[#3194A0]"
                  : "text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Panel Container */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-6 mt-[-5px]">
          {/* SLABS TAB */}
          {activeTab === "slabs" && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* New Regime Table */}
              <div className="flex flex-col h-full">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-bold text-slate-700 dark:text-slate-200 flex items-center gap-2">
                    <ShieldCheck size={18} className="text-[#3194A0]" /> New
                    Regime
                  </h4>
                  <Button
                    variant="ghost"
                    className="h-8 text-xs"
                    type="button"
                    disabled={!canEditTaxConfig}
                    onClick={() => {
                      if (!canEditTaxConfig) return;
                      addSlab(true);
                    }}
                  >
                    <Plus size={14} /> Add Row
                  </Button>
                </div>

                <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden flex-1">
                  <table className="w-full text-sm">
                    <thead className="bg-[#3194A0]/10 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                      <tr>
                        <th className="p-3 text-left pl-4">Min (₹)</th>
                        <th className="p-3 text-left">Max (₹)</th>
                        <th className="p-3 text-center">Rate (%)</th>
                        <th className="w-10" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {newRegimeSlabs.map((slab, i) => {
                        const isLastRow = i === newRegimeSlabs.length - 1;

                        return (
                          <tr
                            key={i}
                            className="group hover:bg-slate-50 dark:hover:bg-slate-800"
                          >
                            <td className="p-2 pl-4">
                              <Input
                                type="number"
                                onWheel={(e) => e.target.blur()}
                                value={slab.min}
                                onChange={(e) =>
                                  updateSlab(i, "min", e.target.value, true)
                                }
                                disabled={!canEditTaxConfig}
                                className="dark:bg-slate-900 dark:border-slate-700 dark:text-white"
                              />
                            </td>

                            <td className="p-2">
                              <Input
                                type="number"
                                onWheel={(e) => e.target.blur()}
                                value={slab.max === null ? "" : slab.max}
                                placeholder={isLastRow ? "∞" : "Max"}
                                disabled={isLastRow || !canEditTaxConfig}
                                onChange={(e) =>
                                  updateSlab(i, "max", e.target.value, true)
                                }
                                className={`dark:bg-slate-900 dark:border-slate-700 dark:text-white ${
                                  isLastRow
                                    ? "opacity-60 cursor-not-allowed"
                                    : ""
                                }`}
                              />
                            </td>

                            <td className="p-2 text-center">
                              <Input
                                type="number"
                                onWheel={(e) => e.target.blur()}
                                value={slab.rate}
                                onChange={(e) => {
                                  if (!canEditTaxConfig) return;
                                  updateSlab(i, "rate", e.target.value, true);
                                }}
                                disabled={!canEditTaxConfig}
                                className="w-16 mx-auto dark:bg-slate-900 dark:border-slate-700 dark:text-white"
                              />
                            </td>

                            <td className="p-2 text-center">
                              <button
                                type="button"
                                disabled={!canEditTaxConfig}
                                onClick={() => {
                                  if (!canEditTaxConfig) return;
                                  deleteSlab(i, true);
                                }}
                                className="text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 p-1 transition-all"
                              >
                                <Trash2 size={14} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Old Regime Table */}
              <div className="flex flex-col h-full">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-bold text-slate-700 dark:text-slate-200 flex items-center gap-2">
                    <History size={18} className="text-slate-400" /> Old Regime
                  </h4>
                  <Button
                    variant="ghost"
                    className="h-8 text-xs"
                    type="button"
                    disabled={!canEditTaxConfig}
                    onClick={() => {
                      if (!canEditTaxConfig) return;
                      addSlab(false);
                    }}
                  >
                    <Plus size={14} /> Add Row
                  </Button>
                </div>

                <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden flex-1">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                      <tr>
                        <th className="p-3 text-left pl-4">Min (₹)</th>
                        <th className="p-3 text-left">Max (₹)</th>
                        <th className="p-3 text-center">Rate (%)</th>
                        <th className="w-10" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {oldRegimeSlabs.map((slab, i) => {
                        const isLastRow = i === oldRegimeSlabs.length - 1;

                        return (
                          <tr
                            key={i}
                            className="group hover:bg-slate-50 dark:hover:bg-slate-800"
                          >
                            <td className="p-2 pl-4">
                              <Input
                                type="number"
                                onWheel={(e) => e.target.blur()}
                                value={slab.min}
                                onChange={(e) =>
                                  updateSlab(i, "min", e.target.value, false)
                                }
                                disabled={!canEditTaxConfig}
                                className="dark:bg-slate-900 dark:border-slate-700 dark:text-white"
                              />
                            </td>

                            <td className="p-2">
                              <Input
                                type="number"
                                onWheel={(e) => e.target.blur()}
                                value={slab.max === null ? "" : slab.max}
                                placeholder={isLastRow ? "∞" : "Max"}
                                disabled={isLastRow || !canEditTaxConfig}
                                onChange={(e) =>
                                  updateSlab(i, "max", e.target.value, false)
                                }
                                className={`dark:bg-slate-900 dark:border-slate-700 dark:text-white ${
                                  isLastRow
                                    ? "opacity-60 cursor-not-allowed"
                                    : ""
                                }`}
                              />
                            </td>

                            <td className="p-2 text-center">
                              <Input
                                type="number"
                                onWheel={(e) => e.target.blur()}
                                value={slab.rate}
                                disabled={!canEditTaxConfig}
                                onChange={(e) =>
                                  updateSlab(i, "rate", e.target.value, false)
                                }
                                className="w-16 mx-auto dark:bg-slate-900 dark:border-slate-700 dark:text-white"
                              />
                            </td>

                            <td className="p-2 text-center">
                              <button
                                type="button"
                                disabled={!canEditTaxConfig}
                                onClick={() => {
                                  if (!canEditTaxConfig) return;
                                  deleteSlab(i, false);
                                }}
                                className="text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 p-1 transition-all"
                              >
                                <Trash2 size={14} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* GENERAL TAB */}
          {activeTab === "general" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h4 className="font-bold text-slate-700 dark:text-slate-200">
                  Deduction Constants (Annual)
                </h4>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <Label className="text-slate-700 dark:text-slate-300">
                      Standard Deduction (New Regime)
                    </Label>
                    <Input
                      disabled={!canEditTaxConfig}
                      type="number"
                      onWheel={(e) => e.target.blur()}
                      className="dark:bg-slate-900 dark:border-slate-700 dark:text-white"
                      {...register("generalRules.newRegimeStd", {
                        valueAsNumber: true,
                      })}
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700 dark:text-slate-300">
                      Standard Deduction (Old Regime)
                    </Label>
                    <Input
                      disabled={!canEditTaxConfig}
                      type="number"
                      onWheel={(e) => e.target.blur()}
                      className="dark:bg-slate-900 dark:border-slate-700 dark:text-white"
                      {...register("generalRules.oldRegimeStd", {
                        valueAsNumber: true,
                      })}
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700 dark:text-slate-300">
                      Rebate Limit (Sec 87A - New)
                    </Label>
                    <Input
                      disabled={!canEditTaxConfig}
                      type="number"
                      onWheel={(e) => e.target.blur()}
                      className="dark:bg-slate-900 dark:border-slate-700 dark:text-white"
                      {...register("generalRules.newRegimeRebateLimit", {
                        valueAsNumber: true,
                      })}
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700 dark:text-slate-300">
                      Rebate Limit (Sec 87A - Old)
                    </Label>
                    <Input
                      disabled={!canEditTaxConfig}
                      type="number"
                      onWheel={(e) => e.target.blur()}
                      className="dark:bg-slate-900 dark:border-slate-700 dark:text-white"
                      {...register("generalRules.oldRegimeRebateLimit", {
                        valueAsNumber: true,
                      })}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-bold text-slate-700 dark:text-slate-200">
                  Cess & Surcharge
                </h4>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <Label className="text-slate-700 dark:text-slate-300">
                      Health & Edu Cess (%)
                    </Label>
                    <Input
                      disabled={!canEditTaxConfig}
                      type="number"
                      onWheel={(e) => e.target.blur()}
                      className="dark:bg-slate-900 dark:border-slate-700 dark:text-white"
                      {...register("generalRules.cess", {
                        valueAsNumber: true,
                      })}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* EXEMPTIONS TAB */}
          {activeTab === "exemptions" && (
            <div className="animate-in fade-in duration-300">
              <div className="flex justify-between items-center mb-4">
                <h4 className="font-bold text-slate-700 dark:text-slate-200">
                  Tax Component Validity
                </h4>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Toggle which deductions are allowed per regime
                </p>
              </div>

              <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 uppercase text-xs">
                    <tr>
                      <th className="p-4 text-left">Component</th>
                      <th className="p-4 text-center w-32">Section</th>
                      <th className="p-4 text-center w-32">New Regime</th>
                      <th className="p-4 text-center w-32">Old Regime</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                    {exemptions.map((ex, i) => (
                      <tr
                        key={ex.id}
                        className="hover:bg-slate-50 dark:hover:bg-slate-800"
                      >
                        <td className="p-4 font-medium text-slate-800 dark:text-slate-200">
                          {ex.name}
                        </td>
                        <td className="p-4 text-center text-slate-500 dark:text-slate-400">
                          {ex.section}
                        </td>
                        <td className="p-4 text-center">
                          <Switch
                            disabled={!canEditTaxConfig}
                            checked={ex.newRegime}
                            onCheckedChange={() => {
                              if (!canEditTaxConfig) return;
                              toggleExemption(i, "newRegime");
                            }}
                          />
                        </td>
                        <td className="p-4 text-center">
                          <Switch
                            disabled={!canEditTaxConfig}
                            checked={ex.oldRegime}
                            onCheckedChange={() => {
                              if (!canEditTaxConfig) return;
                              toggleExemption(i, "oldRegime");
                            }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4 pt-6">
          <Button
            type="submit"
            disabled={!canEditTaxConfig || savingSection === tabId || isSubmitting}
          >
            {savingSection === tabId || isSubmitting
              ? "Saving..."
              : "Save Configuration"}
          </Button>

          <Button type="button" disabled={!canEditTaxConfig} onClick={handleCancel} variant="outline">
            Cancel
          </Button>

          <Button
            type="button"
            variant="destructive"
            onClick={() => {
              if (!canEditTaxConfig) return;
              handleDisable();
            }}
            className="ml-auto"
            disabled={savingSection === tabId || !canEditTaxConfig}
          >
            Disable TDS
          </Button>
        </div>
      </form>
    </div>
  );
};

export default TdsConfiguration;
